import { useCallback } from 'react';
import { callBackend } from '../../../utils/api';
import { buildFactorMatrixScopePayload } from '../dashboardUtils';

const CLOUD_RUN_ERROR_PATTERN = /Cloud Run call failed:\s*(\d+)\s*-\s*(.*)$/;
const LEGACY_IDENTITY_CODES = new Set(['ambiguous_identifier', 'unresolved_identifier']);
const IDENTITY_PARTIAL_STATUS = 'IDENTITY_PARTIAL';
const IDENTITY_UNRESOLVED_ALL_STATUS = 'IDENTITY_UNRESOLVED_ALL';
const SUCCESS_STATUS = 'SUCCESS';

const normalizeText = (value) => {
    if (value == null) {
        return '';
    }
    return String(value).trim();
};

const parseCloudRunIdentityError = (error) => {
    if (error?.name === 'BackendHttpError') {
        return {
            statusCode: error.status,
            code: normalizeText(error.code).toLowerCase(),
            identityResolution: error.detail?.identity_resolution ?? error.detail,
        };
    }
    const rawMessage = error instanceof Error ? error.message : normalizeText(error);
    const match = rawMessage.match(CLOUD_RUN_ERROR_PATTERN);
    if (!match) {
        return {
            statusCode: null,
            code: '',
            identityResolution: null,
        };
    }

    const statusCode = Number(match[1]);
    const payloadText = normalizeText(match[2]);

    let parsedPayload = null;
    try {
        parsedPayload = payloadText ? JSON.parse(payloadText) : null;
    } catch {
        parsedPayload = null;
    }

    const detail = parsedPayload && typeof parsedPayload === 'object' && 'detail' in parsedPayload
        ? parsedPayload.detail
        : parsedPayload;
    const normalizedDetail = detail && typeof detail === 'object' ? detail : null;
    const identityResolution = normalizedDetail?.identity_resolution
        && typeof normalizedDetail.identity_resolution === 'object'
        ? normalizedDetail.identity_resolution
        : normalizedDetail;

    return {
        statusCode: Number.isFinite(statusCode) ? statusCode : null,
        code: normalizeText(normalizedDetail?.code).toLowerCase(),
        identityResolution,
    };
};

const normalizeStartupIdentityStatus = ({
    status = '',
    success = null,
    identityResolution = null,
    hasResolvedRows = false,
} = {}) => {
    const normalizedStatus = normalizeText(status).toUpperCase();
    if (
        normalizedStatus === SUCCESS_STATUS
        || normalizedStatus === IDENTITY_PARTIAL_STATUS
        || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS
    ) {
        return normalizedStatus;
    }
    if (normalizedStatus) {
        return normalizedStatus;
    }

    const identityCode = normalizeText(identityResolution?.code).toLowerCase();
    if (LEGACY_IDENTITY_CODES.has(identityCode)) {
        return hasResolvedRows ? IDENTITY_PARTIAL_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }

    if (success === false) {
        return hasResolvedRows ? IDENTITY_PARTIAL_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }
    if (success === true) {
        return hasResolvedRows ? SUCCESS_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }
    return hasResolvedRows ? SUCCESS_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
};

const buildNonFatalIdentityMessage = ({
    status,
    responseMessage,
} = {}) => {
    const normalizedStatus = normalizeStartupIdentityStatus({ status, success: null, hasResolvedRows: false });
    if (normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS) {
        return (
            responseMessage
            || 'No securities resolved into trade-ideas identity scope. Review MSI drift choices and retry.'
        );
    }
    return (
        responseMessage
        || 'Some trade idea rows could not be identity-resolved; showing resolved rows only.'
    );
};

export default function useTradeIdeasWorkflow({
    userUid,
    selectedFactorModel,
    selectedBetaSource,
    factorSnapshotDate,
    factorDataModel,
    idioVolSource,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    setActualPortTradeIdeas,
    setError,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
}) {
    const fetchTradeIdeasFromGmvActuals = useCallback(async (
        matricesOverride = null,
        {
            isCurrentRequest = null,
        } = {},
    ) => {
        if (!userUid) return [];
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) {
            return [];
        }

        try {
            const body = {
                risk_source: selectedFactorModel,
                beta_source: selectedBetaSource,
                factor_snapshot_date: matricesOverride?.factor_snapshot_date ?? factorSnapshotDate,
                factor_data_model: matricesOverride?.factor_data_model ?? factorDataModel,
                idio_vol_source: idioVolSource,
                default_target_vol_pct: defaultTargetVolPct,
                default_target_vol_dollar: defaultTargetVolDollar,
                default_scaler: defaultScaler,
                ...buildFactorMatrixScopePayload(matricesOverride),
            };

            const result = await callBackend(
                "get_trade_ideas_from_gmv_actuals",
                "POST",
                body,
            );
            if (!isCurrent()) {
                return [];
            }

            const hasExplicitDataArray = Array.isArray(result?.data) || Array.isArray(result);
            const data = Array.isArray(result?.data)
                ? result.data
                : Array.isArray(result)
                    ? result
                    : [];
            const identityResolution = result?.identity_resolution;
            const normalizedStatus = normalizeStartupIdentityStatus({
                status: result?.status,
                success: result?.success,
                identityResolution,
                hasResolvedRows: data.length > 0,
            });
            const isIdentityNonFatal =
                normalizedStatus === IDENTITY_PARTIAL_STATUS
                || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS;
            const canRenderData =
                result?.success === true
                || normalizedStatus === SUCCESS_STATUS
                || isIdentityNonFatal
                || data.length > 0;

            if (canRenderData && (hasExplicitDataArray || isIdentityNonFatal || normalizedStatus === SUCCESS_STATUS)) {
                const normalized = data.map((row) => ({
                    ...row,
                    display_sedol: row?.display_sedol ?? row?.sedol ?? null,
                    sedol: row?.display_sedol ?? row?.sedol ?? null,
                    id: row?.cognive_id ?? row?.id ?? row?.ticker ?? null,
                }));
                setActualPortTradeIdeas(normalized);
                setError(null);
                if (isIdentityNonFatal) {
                    setSnackbarMessage(
                        buildNonFatalIdentityMessage({
                            status: normalizedStatus,
                            responseMessage: result?.message,
                        }),
                    );
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                }
                return normalized;
            } else {
                setError("Invalid data structure from get_trade_ideas_from_gmv_actuals.");
                setSnackbarMessage(result?.message || "Failed to load trade ideas.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return [];
            }
        } catch (error) {
            if (!isCurrent()) {
                return [];
            }
            const parsedError = parseCloudRunIdentityError(error);
            if (parsedError.statusCode === 409 && LEGACY_IDENTITY_CODES.has(parsedError.code)) {
                const normalizedStatus = normalizeStartupIdentityStatus({
                    status: '',
                    success: false,
                    identityResolution: parsedError.identityResolution,
                    hasResolvedRows: false,
                });
                setActualPortTradeIdeas([]);
                setError(null);
                setSnackbarMessage(
                    buildNonFatalIdentityMessage({
                        status: normalizedStatus,
                    }),
                );
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
                return [];
            }
            console.error("Error fetching trade ideas:", error);
            setError("Failed to load trade ideas. Please try again.");
            setSnackbarMessage("Failed to load trade ideas.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return [];
        }
    }, [
        defaultScaler,
        defaultTargetVolDollar,
        defaultTargetVolPct,
        factorDataModel,
        factorSnapshotDate,
        idioVolSource,
        selectedBetaSource,
        selectedFactorModel,
        setActualPortTradeIdeas,
        setError,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    return {
        fetchTradeIdeasFromGmvActuals,
    };
}
